import { useMemo } from 'react';
import type { LobbyState, WSClientMsg } from '@geoguess/shared';
import { RotateCcw, Trophy } from 'lucide-react';
import type { AuthedUser } from '../discord.js';
import { PlayerAvatar } from '../components/PlayerAvatar.js';
import { Logo } from '../components/Logo.js';

export function FinalScreen({
  lobby,
  me,
  isHost,
  send,
}: {
  lobby: LobbyState;
  me: AuthedUser;
  isHost: boolean;
  send: (msg: WSClientMsg) => void;
}) {
  const ranking = useMemo(
    () =>
      Object.values(lobby.players)
        .map((p) => ({ userId: p.userId, name: p.name, avatar: p.avatar, total: p.score }))
        .sort((a, b) => b.total - a.total),
    [lobby.players],
  );
  const podium = ranking.slice(0, 3);
  const rest = ranking.slice(3);
  // visual order: 2nd, 1st, 3rd
  const podiumOrder = [podium[1], podium[0], podium[2]].filter(Boolean);
  const heights = ['h-28', 'h-40', 'h-20'];
  const placeByVisual = [1, 0, 2];
  const placeColors = ['bg-panel-3', 'bg-warn', 'bg-[#7d4f29]'];

  return (
    <div className="bg-hero flex h-full flex-col overflow-auto">
      <header className="flex items-center justify-between px-5 pt-5 pb-2">
        <div className="flex items-center gap-2.5">
          <Logo size={26} />
          <h1 className="text-lg font-bold tracking-tight">Wo Ist?</h1>
        </div>
        <span className="rounded-full bg-panel/80 px-3 py-1 text-xs font-medium text-muted backdrop-blur">
          Endstand
        </span>
      </header>

      <div className="flex flex-1 flex-col items-center gap-8 px-6 py-6">
        <div className="anim-pop flex flex-col items-center gap-2 text-center">
          <div className="grid h-14 w-14 place-items-center rounded-full bg-warn/15 ring-1 ring-warn/40">
            <Trophy className="h-7 w-7 text-warn" />
          </div>
          <h2 className="text-3xl font-bold tracking-tight">Gefunden!</h2>
          {ranking[0] && (
            <p className="text-sm text-muted">
              <span className="font-semibold text-fg">{ranking[0].name}</span> gewinnt mit{' '}
              <span className="font-mono tabular-nums">{ranking[0].total}</span> Punkten
            </p>
          )}
        </div>

        <div className="flex w-full max-w-3xl items-end justify-center gap-4">
          {podiumOrder.map((p, i) => {
            if (!p) return null;
            const place = placeByVisual[i]!;
            const color = placeColors[place]!;
            return (
              <div
                key={p.userId}
                className="anim-slide-up flex flex-1 flex-col items-center"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <PlayerAvatar
                  name={p.name}
                  url={p.avatar || undefined}
                  size={place === 0 ? 80 : 60}
                  ring={p.userId === me.userId}
                />
                <div className="mt-2 text-center">
                  <div className="truncate font-semibold">{p.name}</div>
                  <div className="font-mono text-[13px] text-muted tabular-nums">
                    {p.total} Pkt
                  </div>
                </div>
                <div
                  className={`relative mt-3 ${heights[i]} w-full overflow-hidden rounded-t-2xl bg-panel`}
                >
                  <div
                    className={`absolute inset-x-0 bottom-0 h-1.5 ${color}`}
                    aria-hidden
                  />
                  <div className="grid h-full place-items-center text-3xl font-bold text-fg">
                    {place + 1}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {rest.length > 0 && (
          <ul className="row-divide w-full max-w-xl rounded-2xl bg-panel">
            {rest.map((p, idx) => (
              <li
                key={p.userId}
                className="flex items-center gap-3 px-4 py-3"
              >
                <span className="w-6 text-right font-mono text-[12px] font-semibold text-muted-2 tabular-nums">
                  {idx + 4}
                </span>
                <PlayerAvatar
                  name={p.name}
                  url={p.avatar || undefined}
                  size={32}
                  ring={p.userId === me.userId}
                />
                <span className="flex-1 truncate text-[14px]">{p.name}</span>
                <span className="font-mono text-[14px] font-semibold tabular-nums">
                  {p.total}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <footer className="sticky bottom-0 border-t border-border bg-bg/80 px-4 py-3 backdrop-blur">
        <button
          type="button"
          disabled={!isHost}
          onClick={() => send({ t: 'restart' })}
          className="press inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-accent px-5 py-3.5 text-[16px] font-semibold text-white shadow-[0_8px_24px_rgba(10,132,255,0.35)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:bg-panel-2 disabled:text-muted-2 disabled:shadow-none"
        >
          <RotateCcw className="h-4 w-4" />
          {isHost ? 'Neue Runde' : 'Warte auf Host…'}
        </button>
      </footer>
    </div>
  );
}
